import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import BASE_URL from "../../config/apiConfig";
import axios from "axios";
import { getUserEmail } from "../../utils/auth";
import "./AddFarmPage.css";
/* global google */

const AddFarmPage = () => {
  const { state } = useLocation();
  const { cropName, landSize, razorpayOrderId, planName, transactionId } =
    state || {};
  const [farmName, setFarmName] = useState("");
  const [sowingDate, setSowingDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedArea, setSelectedArea] = useState(0); // Stores area in hectares

  const navigate = useNavigate();
  const mapRef = useRef(null);
  const searchBoxRef = useRef(null); // Ref for search input box
  const polygonCoords = useRef([]); // Ref to store coordinates
  const polygonRef = useRef(null); // Ref to store the polygon
  const map = useRef(null); // Use map as a ref to persist the map instance

  const DEFAULT_LOCATION = { lat: 37.7749, lng: -122.4194 }; // Default location (San Francisco)

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry,places,marker`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google) {
          initMap();
        } else {
          setErrorMessage(
            "Google Maps failed to load. Please try again later."
          );
        }
      };
      script.onerror = () =>
        setErrorMessage("Failed to load Google Maps. Please try again later.");
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script); // Cleanup script
      };
    };

    loadGoogleMapsScript();
  }, []);

  const initMap = () => {
    getUserLocation()
      .then((location) => {
        initializeMap(location);
      })
      .catch(() => {
        initializeMap(DEFAULT_LOCATION); // Fall back to default location
      });
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            resolve(location);
          },
          async () => {
            try {
              const ipLocation = await fetchIpLocation();
              resolve(ipLocation);
            } catch {
              reject("Failed to retrieve location");
            }
          }
        );
      } else {
        reject("Geolocation not available");
      }
    });
  };

  const fetchIpLocation = async () => {
    try {
      const response = await axios.get("https://ipapi.co/json/");
      if (response.status === 200 && response.data) {
        return { lat: response.data.latitude, lng: response.data.longitude };
      } else {
        throw new Error("IP location not available");
      }
    } catch (error) {
      console.error("Failed to fetch IP location:", error);
      throw error;
    }
  };

  const initializeMap = (center) => {
    map.current = new google.maps.Map(mapRef.current, {
      zoom: 15,
      center: center,
      mapId: "520ede0eb4624c69",
      mapTypeId: google.maps.MapTypeId.HYBRID,
    });

    polygonRef.current = new google.maps.Polygon({
      paths: polygonCoords.current,
      strokeColor: "#FF0000",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#FF0000",
      fillOpacity: 0.35,
      geodesic: true,
    });

    polygonRef.current.setMap(map.current);

    map.current.addListener("click", (event) => {
      addMarker(event.latLng);
      addPolygonPoint(event.latLng);
    });

    // Initialize the search box
    const input = searchBoxRef.current;
    const searchBox = new google.maps.places.SearchBox(input);
    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (places.length === 0) return;

      const bounds = new google.maps.LatLngBounds();
      places.forEach((place) => {
        if (place.geometry && place.geometry.location) {
          bounds.extend(place.geometry.location);
          map.current.setCenter(place.geometry.location);
          map.current.setZoom(15);
        }
      });
      map.current.fitBounds(bounds);
    });
  };

  const addMarker = (location) => {
    if (!map.current.markers) {
      map.current.markers = []; // Initialize the markers array if it doesn't exist
    }

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: map.current,
      position: location,
      title: "Click Marker",
    });

    map.current.markers.push(marker); // Add marker to the markers array
  };

  const addPolygonPoint = (location) => {
    polygonCoords.current.push({
      lat: location.lat(),
      lng: location.lng(),
    });

    if (polygonRef.current) {
      polygonRef.current.setPath(polygonCoords.current);
      updateArea(); // Recalculate area when a new point is added
    } else {
      console.error("Polygon is not initialized.");
    }
  };

  const updateArea = () => {
    if (polygonCoords.current.length < 3) {
      setSelectedArea(0); // Reset area if less than 3 points
      return;
    }

    const polygon = new google.maps.Polygon({ paths: polygonCoords.current });
    const areaInSquareMeters = google.maps.geometry.spherical.computeArea(
      polygon.getPath()
    );
    const areaInHectares = (areaInSquareMeters / 10000).toFixed(2);

    setSelectedArea(areaInHectares); // Update the state with the new area
  };

  const clearAll = () => {
    // Clear all advanced markers
    if (map.current.markers) {
      map.current.markers.forEach((marker) => (marker.map = null)); // Remove each marker from the map
    }
    map.current.markers = []; // Reset the markers array

    // Clear polygon coordinates
    polygonCoords.current = []; // Clear the stored coordinates
    if (polygonRef.current) {
      polygonRef.current.setPath([]); // Clear the polygon path on the map
    }

    // Clear any error messages
    setSelectedArea(0);
    setErrorMessage("");
  };

  const handleGeocode = () => {
    const query = searchBoxRef.current.value;
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: query }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        // Ensure the location is a valid LatLng
        if (location instanceof google.maps.LatLng) {
          map.current.setCenter(location);
          map.current.setZoom(15);
          addMarker(location);
        } else {
          setErrorMessage("Invalid location coordinates.");
        }
      } else {
        setErrorMessage("Place not found. Please try another search.");
      }
    });
  };
  const fetchCropCode = async (cropName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-crop-code`,
        {
          cropName,
        }
      );
      if (response.data.success) {
        return response.data.cropCode;
      } else {
        setErrorMessage(response.data.message || "Failed to fetch crop code.");
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch crop code:", error);
      setErrorMessage("Failed to fetch crop code. Please try again later.");
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!farmName || !sowingDate) {
      setErrorMessage("Please complete all fields.");
      return;
    }

    setErrorMessage("");
    setLoading(true);

    try {
      const cropCode = await fetchCropCode(cropName);
      if (!cropCode) {
        setLoading(false);
        return;
      }

      const polygon = polygonRef.current;
      if (!polygonCoords.current.length) {
        setErrorMessage(
          "Polygon is not defined. Please select the area again."
        );
        setLoading(false);
        return;
      }

      const coordinates = polygon
        .getPath()
        .getArray()
        .map((latlng) => ({
          lng: latlng.lng(),
          lat: latlng.lat(),
        }));

      if (coordinates.length < 3) {
        setErrorMessage(
          "Please select at least 3 coordinates for the farm area."
        );
        setLoading(false);
        return;
      }

      const areaInHectares = calculateAreaInHectares(polygon.getPath());

      const areaInHectaresNum = Number(areaInHectares);
      const landSizeNum = Number(landSize);

      console.log("Debug - Area in Hectares:", areaInHectares);
      console.log("Debug - Land Size from Subscription:", landSize);
      console.log("Debug - Type of areaInHectares:", typeof areaInHectares);
      console.log("Debug - Type of landSize:", typeof landSize);

      if (areaInHectaresNum <= landSizeNum) {
        console.log(
          "Area in hectares:",
          areaInHectares,
          "and land size:",
          landSize
        );

        const fieldData = {
          UID: process.env.REACT_APP_UID,
          CropCode: cropCode,
          FieldName: farmName,
          PaymentType: 1,
          Points: coordinates.map((point) => [point.lat, point.lng]),
          SowingDate: sowingDate,
          email: getUserEmail(),
          razorpayOrderId,
          planName,
          transactionId,
        };

        const response = await axios.post(
          `${BASE_URL}/api/user/farm-management/add-farm`,
          fieldData
        );
        if (response.data.success) {
          navigate("/user/crop-health-management");
        } else {
          setErrorMessage(response.data.message || "Failed to submit data.");
        }
      } else {
        setErrorMessage(
          `The selected area is ${areaInHectares} hectares, but the remaining allowed land size is ${landSize} hectares.`
        );
      }
    } catch (error) {
      console.error("Error adding farm:", error);
      setErrorMessage("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const calculateAreaInHectares = (path) => {
    const areaInSquareMeters = google.maps.geometry.spherical.computeArea(path);
    return (areaInSquareMeters / 10000).toFixed(2);
  };

  return (
    <div className="add-farm-page">
      <h3>Select Farm Area</h3>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <input
          id="search-box"
          ref={searchBoxRef}
          type="text"
          placeholder="Search location"
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
        <button
          id="search-btn"
          onClick={handleGeocode}
          style={{
            padding: "10px",
            border: "none",
            backgroundColor: "#51833a",
            color: "#fff",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Search
        </button>
      </div>
      <div
        id="map"
        ref={mapRef}
        style={{
          width: "100%",
          height: "400px",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        }}
      ></div>
      <div className="selected-area">
        <strong>Selected Area:</strong> {selectedArea} hectares
      </div>

      <div className="form-container">
        <label>
          Farm Name:
          <input
            type="text"
            value={farmName}
            onChange={(e) => setFarmName(e.target.value)}
            placeholder="Enter farm name"
          />
        </label>
        <label>
          Crop:
          <input type="text" value={cropName} disabled />
        </label>
        <label>
          Sowing Date:
          <input
            type="date"
            value={sowingDate}
            onChange={(e) => setSowingDate(e.target.value)}
            max={new Date().toISOString().split("T")[0]}
          />
        </label>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <LoadingButton
          fullWidth
          size="large"
          type="button"
          onClick={handleSubmit}
          color="inherit"
          variant="contained"
          loading={loading}
          disabled={!farmName || !sowingDate}
          sx={{
            background: "linear-gradient(45deg, #51833a 30%, #66bb6a 90%)",
            color: "#fff",
            "&:hover": { backgroundColor: "#406027" },
            mb: 2,
            textTransform: "none",
            minHeight: "50px",
            borderRadius: "8px",
            transition: "0.3s ease",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          Save Farm
        </LoadingButton>
        <button onClick={clearAll} className="clear-btn">
          Clear All
        </button>
      </div>
    </div>
  );
};

export default AddFarmPage;
